import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, BarChart3, Building2, Eye, Mail, Phone, Users } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import useAuth from '@/features/auth/hooks/use-auth'
import useAxiosPrivate from '@/features/auth/hooks/use-axios-private'
import { clientRouteId } from '@/features/clients/utils/client-route-id'
import {
  useMyStateTransitionsV2Query,
  useNegotiationsAdminV2Summary,
  useRecentNegotiationsV2Query
} from '@/features/negotiations/hooks/use-negotiations-admin-v2-query'
import { statesWithCounts } from '@/features/negotiations/operations/states-with-counts'
import { EnumTypeStates } from '@/features/states/models/state.schema'
import { Actions } from '@/features/workflows/lib/consts'
import type { TypeClient } from '@/shared/catalogs/type-client'
import { formatCode } from '@/shared/constants/config'
import { queryKeys } from '@/shared/constants/query-keys'
import { Badge } from '@/shared/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Skeleton } from '@/shared/ui/skeleton'
import { TypographyH2 } from '@/shared/ui/typography/h2'
import { formatMoney } from '@/shared/utils/format-money'

/** Cuántas negociaciones recientes pinta la tarjeta. */
const RECENT_NEGOTIATIONS = 5

type DashboardStats = {
  totalNegotiations: number
  activeNegotiations: number
  propertiesWithNegotiations: number
}

type RecentClient = {
  _id: string
  id: string
  /** `cli_…`: el id con el que se abre el expediente. Ver `clientRouteId`. */
  publicId?: string | null
  fullname: string
  email?: string
  phone?: string
  createdAt: string
  typePerson: TypeClient
}

const Dashboard = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const { auth } = useAuth()

  const getGreeting = () => {
    const hour = new Date().getHours()
    const name = auth?.fullname || 'Usuario'
    if (hour < 12) return `🌅 Buenos días, ${name}`
    if (hour < 18) return `☀️ Buenas tardes, ${name}`
    return `🌙 Buenas noches, ${name}`
  }

  const handleStateClick = (stateId: string) => {
    navigate(PATHS.admin.negotiations.withState(stateId))
  }

  const handleNegotiationClick = (negotiationId: string) => {
    navigate(PATHS.admin.negotiations.view(negotiationId))
  }

  const handleClientClick = (clientId: string, type: TypeClient) => {
    navigate(PATHS.admin.clients.byType(type, Actions.view, clientId))
  }

  /**
   * LAS TRES CONSULTAS DE NEGOCIACIONES SALEN DE v2, y ninguna necesitó endpoint
   * nuevo: los estados con su color de `/me/state-transitions`, los conteos y las
   * métricas de `/admin/negotiations/summary`, y las últimas cinco del listado
   * ordenado. Las tres rutas v1 que las servían —`count-by-state`,
   * `dashboard/metrics` y `dashboard/recent-negotiations`— se retiraron con esto.
   *
   * De paso se van los tres `catch` que devolvían `[]`: un fallo de red pintaba
   * «No hay datos», que en un dashboard se lee como «no hay trabajo». Ahora el
   * error sube y la pantalla decide, como pide el ADR 0031.
   */
  const { data: stateTransitions, isLoading: isLoadingStates } = useMyStateTransitionsV2Query()
  const {
    data: summary,
    isLoading: isLoadingSummary,
    isError: isSummaryError
  } = useNegotiationsAdminV2Summary({})
  const {
    data: recentPage,
    isLoading: isLoadingNegotiations,
    isError: isRecentError
  } = useRecentNegotiationsV2Query(RECENT_NEGOTIATIONS)

  const isLoadingNegotiationsByState = isLoadingStates || isLoadingSummary

  /**
   * El conteo por estado se arma cruzando DOS respuestas, y no es un rodeo: el
   * summary indexa por `sta_` sin nombres —es un agregado, no un catálogo— y los
   * nombres, colores y tipos vienen del mismo endpoint que alimenta los chips del
   * tablero. Cruzarlos acá evita una tercera ruta cuyo único trabajo sería
   * repetir el catálogo.
   *
   * Se muestran sólo los estados que este usuario puede LEER, que es lo que
   * `/me/state-transitions` devuelve: un asesor no ve tarjetas de estados que
   * después no podría abrir.
   */
  const dataNegotiationsByState = useMemo(
    () => statesWithCounts(stateTransitions?.data ?? [], summary?.byState ?? {}),
    [stateTransitions, summary]
  )

  const recentNegotiations = recentPage?.data ?? []

  const { data: recentClients = [], isLoading: isLoadingClients } = useQuery<RecentClient[]>({
    queryKey: queryKeys.dashboard.recentClients(),
    queryFn: async () => {
      try {
        const response = await axiosPrivate.get('/clients/dashboard/recent-clients')
        return response.data
      } catch (e) {
        console.error(e)
        return []
      }
    },
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  /**
   * `totalNegotiations` sale de `summary.total` y NO de sumar las tarjetas: el
   * total cuenta también las filas cuyo estado no resuelve, así que la suma de
   * las facetas es siempre menor o igual. Sumarlas era la fuente de un número que
   * no coincide con el del tablero, con las dos pantallas a un clic de distancia.
   */
  const dashboardStats: DashboardStats = useMemo(
    () => ({
      totalNegotiations: summary?.total ?? 0,
      activeNegotiations: dataNegotiationsByState
        .filter(
          (state) => state.type !== EnumTypeStates.end && state.type !== EnumTypeStates.rescind
        )
        .reduce((sum, state) => sum + state.negotiations, 0),
      propertiesWithNegotiations: summary?.propertiesWithNegotiations ?? 0
    }),
    [dataNegotiationsByState, summary]
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  return (
    <div className="py-6 lg:py-9 px-6 lg:px-14 space-y-8">
      {/* Saludo dinámico */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">{getGreeting()}</h1>
        <p className="text-muted-foreground mt-2">
          Aquí tienes un resumen de tus actividades más recientes
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 min-w-0">
        {/* Estados compactos en doble columna */}
        <div>
          <h3 className="text-base font-semibold mb-3">Estados de Negociaciones</h3>
          {isLoadingNegotiationsByState && dataNegotiationsByState.length === 0 && (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 min-w-0">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-2">
                    <div className="flex items-center justify-between">
                      <div className="h-3 bg-muted rounded w-20" />
                      <div className="h-6 bg-muted rounded w-8" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {(!isLoadingNegotiationsByState || dataNegotiationsByState.length > 0) && (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 min-w-0">
              {dataNegotiationsByState.length > 0 ? (
                dataNegotiationsByState
                  .filter((state) => state.negotiations > 0)
                  .map((state) => (
                    <Card
                      key={state.id}
                      className="cursor-pointer hover:shadow-sm transition-all duration-200 hover:scale-[1.02] group"
                      onClick={() => {
                        handleStateClick(state.id)
                      }}
                    >
                      <CardContent className="p-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              style={{ backgroundColor: state.color }}
                              className="w-2 h-2 rounded-full shrink-0"
                            />
                            <span
                              style={{ color: state.color }}
                              className="font-medium text-xs truncate"
                            >
                              {state.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="font-bold text-lg">{state.negotiations}</span>
                            <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
              ) : (
                <div className="col-span-full">
                  {/* «No hay datos» y «no se pudieron cargar» son cosas distintas y
                      antes decían lo mismo: el `catch` devolvía `[]` y un fallo de
                      red se leía como una empresa sin trabajo. */}
                  <TypographyH2 className="text-base">
                    {isSummaryError ? 'No se pudieron cargar los estados' : 'No hay datos'}
                  </TypographyH2>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Métricas principales compactas */}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 h-fit">
          <h3 className="text-base font-semibold col-span-full">Métricas</h3>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold">
                    {isLoadingNegotiationsByState ? (
                      <Skeleton className="h-7 w-12" />
                    ) : (
                      dashboardStats.totalNegotiations
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Total Negociaciones</p>
                </div>
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold">
                    {isLoadingNegotiationsByState ? (
                      <Skeleton className="h-7 w-12" />
                    ) : (
                      dashboardStats.activeNegotiations
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Negociaciones Activas</p>
                </div>
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold">
                    {isLoadingSummary ? (
                      <Skeleton className="h-7 w-12" />
                    ) : (
                      dashboardStats.propertiesWithNegotiations
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Propiedades en Negociación</p>
                </div>
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Secciones en dos columnas */}
      <div className="grid gap-6 lg:grid-cols-2 min-w-0">
        {/* Mis Últimas Negociaciones */}
        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Últimas Negociaciones
            </CardTitle>
            <Link
              to={PATHS.admin.negotiations.root}
              className="text-sm text-primary hover:underline flex items-center gap-1 transition-colors"
            >
              <Eye className="h-4 w-4" />
              Ver todas
            </Link>
          </CardHeader>
          <CardContent>
            {isLoadingNegotiations ? (
              <div className="space-y-1">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="border rounded-md p-2">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-4 w-12 ml-auto" />
                      </div>
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentNegotiations.length > 0 ? (
              <div className="space-y-1">
                {recentNegotiations.map((negotiation) => {
                  // El titular si lo hay, y si no el primero: la misma regla que
                  // aplicaba el servicio v1 («first default owner or first owner»).
                  // Sin propietarios es un caso REAL —el alta lo permite— y por eso
                  // se nombra en vez de asumir que siempre hay uno.
                  const owner =
                    negotiation.owners.find((candidate) => candidate.default) ??
                    negotiation.owners[0]
                  // Al rescindir, el inmueble se muda a `previousProperty`. Sin este
                  // respaldo la tarjeta saldría sin nombre justo en las rescindidas.
                  const property = negotiation.property ?? negotiation.previousProperty
                  return (
                    <div
                      key={negotiation.id}
                      onClick={() => {
                        handleNegotiationClick(negotiation.id)
                      }}
                      className="rounded-md p-2 hover:bg-muted/50 cursor-pointer transition-colors border-b border-b-muted"
                    >
                      {/* Layout de doble línea en todos los tamaños */}
                      <div className="text-xs">
                        {/* Línea 1: Código, propiedad y estado */}
                        <div className="flex items-center gap-2 min-w-0 flex-1 mb-2">
                          <span className="font-medium text-sm shrink-0">
                            {formatCode(negotiation.code)}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {property?.name ?? 'Sin inmueble'}
                          </span>
                          <Badge
                            style={{
                              backgroundColor: negotiation.currentState.state.color ?? undefined
                            }}
                            className="text-white text-[10px] px-1.5 py-0.5 ml-auto text-center w-fit"
                          >
                            {negotiation.currentState.state.name}
                          </Badge>
                        </div>

                        {/* Línea 2: Cliente y monto */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs truncate max-w-40">
                              {owner?.name ?? 'Sin propietario'}
                            </span>
                          </div>

                          <span className="font-medium text-xs">
                            {/* Sin acuerdo de pago todavía no hay precio, y anunciar
                                Q0 afirma algo falso sobre el negocio. */}
                            {/* El símbolo de la moneda lo pone `formatMoney`: un ícono
                                al lado lo duplicaba («$ Q 1,000,000»). */}
                            {negotiation.totalAmount === null
                              ? 'Sin monto'
                              : formatMoney(negotiation.totalAmount, negotiation.currency, {
                                  currencyDisplay: 'narrowSymbol',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                                })}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {isRecentError
                    ? 'No se pudieron cargar las negociaciones'
                    : 'No tienes negociaciones recientes'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mis Clientes Recientes */}
        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Clientes Recientes
            </CardTitle>
            <Link
              to={PATHS.admin.clients.root}
              className="text-sm text-primary hover:underline flex items-center gap-1 transition-colors"
            >
              <Eye className="h-4 w-4" />
              Ver todos
            </Link>
          </CardHeader>
          <CardContent>
            {isLoadingClients ? (
              <div className="space-y-1">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="border rounded-md p-2">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-12" />
                      </div>
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentClients.length > 0 ? (
              <div className="space-y-1">
                {recentClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => {
                      handleClientClick(clientRouteId(client), client?.typePerson)
                    }}
                    className="rounded-md p-2 hover:bg-muted/50 cursor-pointer transition-colors border-b border-b-muted"
                  >
                    {/* Layout de doble línea en todos los tamaños */}
                    <div className="text-xs">
                      {/* Línea 1: Nombre y fecha */}
                      <div className="flex items-center justify-between gap-2 min-w-0 flex-1 mb-2">
                        <span className="font-medium text-sm truncate">{client.fullname}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDate(client.createdAt)}
                        </span>
                      </div>

                      {/* Línea 2: Información de contacto */}
                      <div className="flex items-center gap-4">
                        {client.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs truncate max-w-40">{client.email}</span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs">{client.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No tienes clientes recientes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Dashboard
